/**
 * E-model (ITU-T G.107) MOS estimate from the per-line RTP stats, simplified for a browser:
 *
 *   d      = rtt/2 + jitter + 10 ms        one-way mouth-to-ear delay (jitter buffer ~ jitter, 10 ms framing)
 *   Id     = 0.024·d + 0.11·(d − 177.3)·H(d − 177.3)        delay impairment (H = step)
 *   Ie-eff = Ie + (95 − Ie) · Ppl / (Ppl + Bpl)             packet-loss impairment (G.107 eq. 7-29)
 *   R      = 93.2 − Id − Ie-eff                              R0 = 93.2 (no other impairments)
 *   MOS    = 1 + 0.035·R + R·(R − 60)·(100 − R)·7·10⁻⁶      for 0 < R < 100, clamped to [1, 4.5]
 *
 * Per-codec Ie / Bpl (G.113 Appendix I, PLC assumed): G.711 0/25.1, G.722 0/25.1 (wideband
 * treated as transparent on the narrowband scale), Opus 5/30 (assumption), unknown 10/16.
 * Thresholds in the UI: green >= 4.0, amber 3.6–4.0, red < 3.6.
 */
export interface MosInput {
  rttMs: number;
  jitterMs: number;
  /** 0..100 */
  lossPct: number;
  codec: string;
}

export interface CodecImpairment {
  Ie: number;
  Bpl: number;
}

export const CODEC_IMPAIRMENT: Record<string, CodecImpairment> = {
  pcmu: { Ie: 0, Bpl: 25.1 },
  pcma: { Ie: 0, Bpl: 25.1 },
  g722: { Ie: 0, Bpl: 25.1 },
  opus: { Ie: 5, Bpl: 30 },
};
export const DEFAULT_IMPAIRMENT: CodecImpairment = { Ie: 10, Bpl: 16 };
export const R0 = 93.2;

export function codecImpairment(codec: string): CodecImpairment {
  const key = codec.toLowerCase().replace(/^audio\//, '');
  return CODEC_IMPAIRMENT[key] ?? DEFAULT_IMPAIRMENT;
}

export function delayImpairment(oneWayMs: number): number {
  const d = Math.max(0, oneWayMs);
  return 0.024 * d + (d > 177.3 ? 0.11 * (d - 177.3) : 0);
}

export function lossImpairment(lossPct: number, c: CodecImpairment): number {
  const ppl = Math.min(100, Math.max(0, lossPct));
  return c.Ie + ((95 - c.Ie) * ppl) / (ppl + c.Bpl);
}

export function rFactor(i: MosInput): number {
  const oneWay = Math.max(0, i.rttMs) / 2 + Math.max(0, i.jitterMs) + 10;
  return R0 - delayImpairment(oneWay) - lossImpairment(i.lossPct, codecImpairment(i.codec));
}

export function rToMos(r: number): number {
  if (r <= 0) return 1;
  if (r >= 100) return 4.5;
  const mos = 1 + 0.035 * r + r * (r - 60) * (100 - r) * 7e-6;
  return Math.min(4.5, Math.max(1, mos));
}

export function eModelMos(i: MosInput): number {
  return Math.round(rToMos(rFactor(i)) * 100) / 100;
}

export type MosBand = 'green' | 'amber' | 'red';
export function mosBand(mos: number): MosBand {
  if (mos >= 4.0) return 'green';
  if (mos >= 3.6) return 'amber';
  return 'red';
}
